import { BackendError, backendFetch, backendJson } from "@/lib/backend";
import type {
  RFIRequestListItem,
  RFIRequestSyncItem,
  CompanyAutocompleteResponse,
} from "@/types/backend";

async function readBodyTextSafe(response: Response): Promise<string> {
  try {
    return await response.text();
  } catch {
    return "";
  }
}

export async function listRfiRequests(
  projectId: string
): Promise<RFIRequestListItem[]> {
  const { response, data } = await backendJson<RFIRequestListItem[]>(
    `/api/projects/${projectId}/rfi/requests`,
    { method: "GET" }
  );

  if (response.ok && data !== null) return data;

  const bodyText = await readBodyTextSafe(response);
  throw new BackendError("Failed to list RFI requests.", {
    status: response.status,
    bodyText,
  });
}

export async function syncRfiRequests(
  projectId: string,
  payload: RFIRequestSyncItem[]
): Promise<RFIRequestListItem[]> {
  const { response, data } = await backendJson<RFIRequestListItem[]>(
    `/api/projects/${projectId}/rfi/requests`,
    {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  if (response.ok && data !== null) return data;

  const bodyText = await readBodyTextSafe(response);
  throw new BackendError("Failed to sync RFI requests.", {
    status: response.status,
    bodyText,
  });
}

function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  // filename*=UTF-8''...
  const mStar = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
  if (mStar?.[1]) {
    try {
      return decodeURIComponent(mStar[1]);
    } catch {
      return mStar[1];
    }
  }
  // filename="..."
  const m = /filename\s*=\s*"([^"]+)"/i.exec(header) ?? /filename\s*=\s*([^;]+)/i.exec(header);
  if (!m?.[1]) return null;
  return m[1].trim();
}

export async function downloadRfiExcel(projectId: string): Promise<void> {
  const response = await backendFetch(`/api/projects/${projectId}/rfi/excel`, {
    method: "GET",
  });

  if (!response.ok) {
    const bodyText = await readBodyTextSafe(response);
    throw new BackendError("Failed to download RFI Excel.", {
      status: response.status,
      bodyText,
    });
  }

  const blob = await response.blob();
  const contentDisposition = response.headers.get("content-disposition");
  const filename =
    filenameFromContentDisposition(contentDisposition) ?? `rfi-${projectId}.xlsx`;

  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function downloadRfiFilesArchive(projectId: string, requestId: string): Promise<void> {
  const response = await backendFetch(
    `/api/projects/${encodeURIComponent(projectId)}/rfi/requests/${encodeURIComponent(requestId)}/files/archive`,
    { method: "GET" },
  );
  if (!response.ok) {
    throw new BackendError("Failed to download RFI files.", {
      status: response.status,
      bodyText: await readBodyTextSafe(response),
    });
  }
  const url = URL.createObjectURL(await response.blob());
  try {
    const link = document.createElement("a");
    link.href = url;
    link.download = filenameFromContentDisposition(response.headers.get("content-disposition")) ?? "RFI.zip";
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function companyAutocomplete(
  q: string,
  signal?: AbortSignal
): Promise<CompanyAutocompleteResponse> {
  const qs = new URLSearchParams({ q }).toString();
  const { response, data } = await backendJson<CompanyAutocompleteResponse>(
    `/api/rfi/company-autocomplete?${qs}`,
    { method: "GET", signal }
  );

  if (response.ok && data !== null) return data;

  const bodyText = await readBodyTextSafe(response);
  throw new BackendError("Failed to autocomplete company names.", {
    status: response.status,
    bodyText,
  });
}
